import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import { Navigate } from "react-router-dom";

import moment from "moment";

import { withRouter } from "../../../with-router";
import GameService from "../../../services/game";
import OnsiteGate from "../../../components/OnsiteGate";
import AuthService from "../../../services/auth";
import FirebaseService from "../../../services/firebase";

import GameTimeout from "../../../components/GameTimeout";
import ErrorModal from "../../../components/ErrorModal";
import SurvivorClockBar from "../../../components/SurvivorClockBar";
import { readRemoteSession } from "../../../context/RemoteLaunchContext";
import { exitGame } from "../../../utils/exitGame";
import { isWebPlay } from "../../../utils/webPlay";
import { periodAndDeadline } from "../../../utils/pickDeadline";

import "./PrePick.css";
import { visitOver } from "../../../services/visit";

/**
 * Survivor Pool — the pre-pick entry manager (SUR-401…SUR-451).
 *
 * Where a player lands after identifying themselves, in place of the standard
 * lobby, whenever a Survivor promotion is running (SUR-401). It shows every
 * active promotion (SUR-402): Survivor ones as a season grid, the rest as a
 * name and a way in.
 *
 * **This component decides nothing.** Every cell arrives from the server with
 * its state and its marker already resolved (`build_prepick`). Survival,
 * elimination, whether a period is open, whether today's play is spent — all of
 * it is computed once, server-side, and three screens draw from that same
 * builder: this one, the admin standings and the TV in the room. The only way
 * to stop them disagreeing about who is out is for none of them to work it out
 * for themselves.
 *
 * So the rules are not here, and must not migrate here. What *is* here is
 * presentation: colours, the ticket icon, the countdown, and getting a
 * nineteen-column grid onto a phone.
 */
class PrePick extends Component {
    constructor(props) {
        super(props);

        this.state = {
            page_ready: false,
            redirect: null,
            venue: null,
            player: null,
            promotions: [],
            // SUR-443 — re-rendered on a tick so the countdown moves. Held in
            // state rather than read at render time so React knows to redraw.
            now: moment(),
            // GEO-405 / GEO-406 -- the gate descriptor. This screen stays
            // readable off site; what the gate removes is the PLAY button.
            onsite: null,
            info_modal: {show: false, title: "", text: ""},
            error_modal: {show: false, title: "", text: ""},
            // Five minutes on the web, one on a kiosk — see utils/webPlay.
            // A kiosk is shared and an idle session holds it; a player on their
            // own phone is blocking nobody, and this screen is the one they
            // read to decide which ticket to play.
            //
            // This screen has nowhere gentler to send an idle player, so a
            // timeout here still ends the session. The pick screen returns
            // here instead of exiting; here is where the session ends.
            timeout: isWebPlay() ? 300000 : 60000
        };

        // SUR-451 — the scroll container per Survivor section, keyed by promo
        // id, so each can be positioned on its own current period.
        this.scrollers = {};
    }

    componentDidMount() {
        if(this.props.router.location.state == null){
            this.exitOrHome("error");
            return;
        }

        let venue = this.props.router.location.state.venue;
        let player = this.props.router.location.state.player;

        // This screen is black, however the player got to it. The lobby paints
        // its image onto <html>, the pick screen paints one onto <body>, and
        // arriving here from each used to leave whichever was last set — so the
        // same screen looked different on a first visit and on the way back
        // from a submitted pick. Both are cleared, because both get written
        // elsewhere, and .prepick paints its own black over the top either way.
        this.clearInheritedBackground();

        this.setState({venue: venue, player: player}, () => this.load());

        // SUR-443 — a ~30s tick that re-asks the server, which is what turns a
        // game starting, or another ticket being played, into a changed grid
        // without the player doing anything.
        //
        // It used to only bump `now` and re-render the countdown, while the
        // comment above it claimed the re-ask. So a player sitting on this
        // screen watched a clock move over a grid that never changed.
        this.tick = setInterval(() => {
            this.setState({now: moment()});
            // SUR-443b -- a hidden tab has nobody to show a fresher grid to.
            // Polling one is pure waste, and it is how a tab left open since
            // lunchtime greeted its owner with an error raised hours earlier
            // against a screen they were not looking at.
            //
            // The countdown still ticks, because that is free and local; only
            // the server re-ask is skipped.
            if(document.visibilityState === "hidden") return;
            this.load(null, true);
        }, 30000);

        // Coming back to the tab is the moment the grid is most likely to be
        // stale, so ask then rather than up to thirty seconds later. Between
        // this and the line above, the poll is fresher exactly when somebody
        // is looking and silent when nobody is.
        this.onVisible = () => {
            if(document.visibilityState === "visible") this.load(null, true);
        };
        document.addEventListener("visibilitychange", this.onVisible);
    }

    componentWillUnmount() {
        if(this.tick) clearInterval(this.tick);
        if(this.onVisible){
            document.removeEventListener("visibilitychange", this.onVisible);
        }
        this.clearInheritedBackground();
    }

    /** Both of them: the lobby writes <html>, the game screens write <body>.
     *  Clearing one leaves the other showing. localStorage is untouched, so the
     *  lobby still repaints its own image when the player returns to it. */
    clearInheritedBackground(){
        let html = document.getElementsByTagName("html");
        if(html.length > 0) html[0].style.backgroundImage = "";
        document.body.style.backgroundImage = "";
    }

    /** One call for the whole screen (SUR-402). Fanning out per promotion would
     *  render the page in pieces. */
    load(onDone, quiet){
        // A session can end while this screen is still mounted -- the timeout
        // exits through exitGame, which clears the stored auth, and other tabs
        // and the remote-launch completion screen clear it too. GameService
        // reads `auth.access` unguarded, so polling after that point throws a
        // TypeError out of an interval callback, where nothing catches it: the
        // player gets a runtime-error overlay instead of the lobby.
        //
        // Stop the poll and leave quietly. Whatever cleared the auth is already
        // taking the player somewhere.
        if(AuthService.getCurrentAuth() == null){
            if(this.tick) clearInterval(this.tick);
            return;
        }

        // The 30s refresh is silent (SUR-443). A "Loading..." modal twice a
        // minute over a screen the player is reading is worse than a stale
        // grid, and it would eat the taps it covers.
        if(!quiet) this.props.showModal("Loading...");
        GameService.getSurvivorPrePick(this.state.player.id).then((data) => {
            this.setState({
                promotions: data.promotions || [],
                onsite: data.onsite || null,
                page_ready: true
            }, () => {
                this.positionScrollers();
                // Truthiness is not enough: load(true) once passed `true`
                // here and crashed the screen after a successful verify.
                if(typeof onDone === "function") onDone();
            });
        }).catch(error => {
            console.log(error);
            // WEB-413 -- a tab left open since lunchtime is the commonest way
            // to arrive here, and "ask an attendant" is the worst possible
            // answer to it: nothing is broken and no attendant can help.
            if(visitOver(error, this, {page_ready: true})) return;
            this.setState({
                page_ready: true,
                error_modal: {
                    show: true,
                    title: "Something went wrong",
                    text: "We could not load your tickets. Please ask an attendant."
                }
            });
        }).finally(() => {
            if(!quiet) this.props.hideModal();
        });
    }

    /**
     * SUR-451 — open each grid scrolled to its current period, right-justified.
     *
     * Must run after layout, not during render: before first paint the
     * container has no width and scrollLeft silently stays at 0, which lands
     * the player in Week 1 of an eighteen-week season. Two frames, because the
     * first only guarantees the DOM exists and the second that it has been
     * measured.
     */
    positionScrollers(){
        requestAnimationFrame(() => requestAnimationFrame(() => {
            Object.keys(this.scrollers).forEach(promo_id => {
                let box = this.scrollers[promo_id];
                if(!box) return;
                let current = box.querySelector(".is-current");
                if(!current){
                    box.scrollLeft = box.scrollWidth;
                    return;
                }
                // Right-justified: the current period sits at the right edge, so
                // the season already played is what is visible behind it. That
                // history is the thing a player studies before choosing.
                let target = current.offsetLeft + current.offsetWidth - box.clientWidth;
                box.scrollLeft = Math.max(0, target);
            });
        }));
    }

    // ---------------------------------------------------------------- actions

    /** SUR-431 — start or continue a ticket. The server already said this was
     *  allowed by putting the marker here; the pick screen re-checks anyway,
     *  because a marker is a render of a moment and the game may have started
     *  since. */
    onPlay(section, cell, row){
        let promo = {id: section.promo_id, name: section.promo_name, game_type: section.game_type};
        FirebaseService.logEvent("survivor_prepick_play", {
            "venue_id": this.state.venue.id,
            "promo_id": section.promo_id
        });
        this.setState({
            redirect: {
                path: "/survivor",
                state: {
                    venue: this.state.venue,
                    promo: promo,
                    player: this.state.player,
                    // Which life is being played, and for which period. Both are
                    // re-validated server-side; sending them makes the pick
                    // screen a pick screen rather than a second selector.
                    survivor_ticket_id: row.ticket_id,
                    survivor_period_id: cell.period_id,
                    survivor_state: null
                }
            }
        });
    }

    /** SUR-434 — the sun icon explains itself. The wording is the server's and
     *  names the venue's timezone (SUR-305b), so "today" is answerable. */
    onTomorrow(section){
        this.setState({
            info_modal: {
                show: true,
                title: "Come back tomorrow",
                text: section.daily_limit_message
            }
        });
    }

    closeInfo(){
        this.setState({info_modal: {show: false, title: "", text: ""}});
    }

    /** SUR-402 — the other four game types are played from the lobby, which owns
     *  their launch rules. Sending the player there rather than duplicating that
     *  dispatch is the whole point: two copies of a launch state machine drift. */
    onPlayOther(section){
        this.setState({
            redirect: {
                path: "/home",
                state: {
                    venue: this.state.venue,
                    player: this.state.player,
                    auto_play_promo_id: section.promo_id
                }
            }
        });
    }

    exitOrHome(result){
        // Stop the refresh before exitGame clears the auth it depends on.
        // componentWillUnmount is not soon enough: the redirect is a setState,
        // so React unmounts this screen on its own schedule and the 30s tick
        // can fire in the gap -- against a session that no longer has a token.
        if(this.tick) clearInterval(this.tick);

        exitGame({
            remoteCtx: readRemoteSession(),
            setRedirect: (redirect) => this.setState({redirect: redirect}),
            result: result,
            player: this.state.player
        });
    }

    // ---------------------------------------------------------------- render

    /** SUR-441 — counts down to entry_end_time, the submission deadline. Not
     *  event_end_time, which can be days later and would promise time that does
     *  not exist. */
    countdown(section){
        if(!section.promo_period_entry_end_time) return "";
        let ends = moment(section.promo_period_entry_end_time);
        let left = moment.duration(ends.diff(this.state.now));
        if(left.asSeconds() <= 0) return "CLOSED";

        let days = Math.floor(left.asDays());
        if(days >= 1) return `${days}d ${left.hours()}h`;
        if(left.hours() >= 1) return `${left.hours()}h ${left.minutes()}m`;
        return `${left.minutes()}m`;
    }

    /** SUR-301 — a slot that was never started and no longer can be.
     *
     *  Enrolment closes after week one, so from week two on an empty row is not
     *  an opportunity, it is a ticket that will never be played. Left bright
     *  gold it reads as available for the rest of the season. */
    missedEnrolment(row){
        return !row.ticket_id && !row.startable;
    }

    missedEnrolmentMessage(row){
        return `Ticket ${row.slot} was not started within the first entry period`;
    }

    renderTicketIcon(row, section){
        let dead = row.status && row.status !== "Active";
        let missed = this.missedEnrolment(row);
        let spent = dead || missed;

        return (
            <div className={"prepick-ticket" + (spent ? " prepick-ticket-dead" : "")}
                 title={missed ? this.missedEnrolmentMessage(row) : undefined}
                 onClick={missed
                     ? () => this.setState({info_modal: {
                           show: true,
                           title: "Not started",
                           text: this.missedEnrolmentMessage(row)
                       }})
                     : undefined}>
                <div>
                    {/* SUR-411 — the number, and nothing else. The weeks-survived
                        line came off: the row itself shows the season across it,
                        so a count beside it was the same fact twice. */}
                    <span className="prepick-ticket-number">{row.slot}</span>
                </div>
            </div>
        );
    }

    /** The promo the gate verifies against.
     *
     * Verification stamps the *session*, not a promotion, so any gated promo on
     * this screen answers for all of them -- but OnsiteGate needs an id to send.
     * The first section is the one whose grid the player is looking at.
     */
    gatePromoId(){
        let sections = this.state.promotions || [];
        return sections.length ? sections[0].promo_id : null;
    }

    /** GEO-406 -- gated, and this visit has not cleared it. */
    onsiteBlocks(){
        let onsite = this.state.onsite;
        return !!(onsite && onsite.required && !onsite.verified);
    }

    /** Where to send them. Server-worded (GEO-201), so a multi-property venue
     *  names its properties rather than a company nobody can drive to. */
    visitMessage(){
        let onsite = this.state.onsite;
        return (onsite && onsite.visit_message) || "Please visit us to make your picks.";
    }

    renderCell(section, row, cell){
        // SUR-425 — a future period is blank whatever else is true. Nothing to
        // click, nothing to read into.
        if(cell.state === "future"){
            return <div className="prepick-cell prepick-cell-future" />;
        }

        let body = null;
        if(cell.state === "no_pick"){
            // SUR-422 — stamped across the cell like OUT, because it says the
            // same kind of thing: this ticket is finished here.
            body = (
                <span className="prepick-mask">
                    <span className="prepick-nopick">NO PICK</span>
                </span>
            );
        }else if(cell.logo_url){
            body = <img src={cell.logo_url} alt={cell.choice_name} />;
        }else if(cell.choice_name){
            body = <span className="prepick-nopick-text">{cell.choice_name}</span>;
        }

        // SUR-426 / SUR-436 — elimination outranks the pick, and the pick is
        // still shown underneath it.
        let mask = null;
        if(cell.state === "orphaned" || cell.marker === "out"){
            mask = (
                <span className="prepick-mask">
                    <span className="prepick-out">OUT</span>
                </span>
            );
        }

        let classes = ["prepick-cell", `prepick-cell-${cell.state}`];
        let onClick = null;

        if(cell.marker === "final"){
            // SUR-438 — the week is settled, so there is nothing to offer. Not
            // a button: its only outcome would be a refusal.
            classes.push("prepick-marker-final");
            body = <span>FINAL</span>;
            mask = null;
        }else if(cell.marker === "play"){
            classes.push("prepick-marker-play");
            body = <span>PLAY</span>;
            mask = null;
            onClick = () => this.onPlay(section, cell, row);
        }else if(cell.marker === "tomorrow"){
            classes.push("prepick-marker-tomorrow");
            body = <span className="prepick-sun" role="img" aria-label="Come back tomorrow">☀️</span>;
            mask = null;
            onClick = () => this.onTomorrow(section);
        }

        if(onClick){
            return (
                <button type="button" className={classes.join(" ")} onClick={onClick}>
                    {body}
                </button>
            );
        }
        return <div className={classes.join(" ")}>{body}{mask}</div>;
    }

    renderSurvivor(section){
        let periods = section.periods || [];
        return (
            <div className="prepick-section" key={`s${section.promo_id}`}>
                {/* SUR-404 — each stacked grid is headed by its promo name, which
                    is the only thing telling Main apart from Second Chance. */}
                <h2>{section.promo_name}</h2>

                {/* SUR-441 / SUR-442 — one line, and one statement in it. The
                    labels went: "Current Week" over "Week 2" said the same
                    thing twice, and the deadline reads as a sentence rather
                    than as a field. */}
                <div className="prepick-header">
                    {section.picking_closed ? (
                        <span className="prepick-header-value prepick-closed">
                            PICKING IS CLOSED
                        </span>
                    ) : (
                        <span className="prepick-header-value">
                            {periodAndDeadline(
                                section.promo_period_name,
                                section.promo_period_entry_end_time
                            )}
                        </span>
                    )}
                </div>

                {/* SUR-444 — what to do, or when to come back. Composed
                    server-side: "today" and "tomorrow" are the venue's, from
                    its own zone (SUR-305a), and a browser must never be the
                    one deciding which day it is. Empty when there is nothing
                    useful to say. */}
                {section.header_message ? (
                    <p className="prepick-guidance">{section.header_message}</p>
                ) : null}

                <div
                    className="prepick-scroll"
                    ref={(el) => { this.scrollers[section.promo_id] = el; }}
                >
                    <table className="prepick-grid">
                        <thead>
                            <tr>
                                <th className="prepick-cornercell">Ticket</th>
                                {periods.map(period => (
                                    <th
                                        key={period.id}
                                        className={
                                            period.id === section.promo_period_id
                                                ? "is-current" : ""
                                        }
                                    >
                                        {period.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* SUR-410 — every potential ticket, started or not.
                                The empty row is what tells a player another
                                entry is available. */}
                            {(section.tickets || []).map(row => {
                                // GEO-406 -- where a PLAY button would be, an
                                // off-site player gets the instruction instead.
                                // It spans the rest of the row: the columns to
                                // the right are future periods and blank
                                // anyway, and a sentence in one 74px cell wraps
                                // to six lines and pushes the grid out of shape.
                                let cells = row.cells || [];
                                let gateAt = this.onsiteBlocks()
                                    ? cells.findIndex(c => c.marker === "play")
                                    : -1;
                                return (
                                <tr key={row.slot}>
                                    <td className="prepick-tickethead">
                                        {this.renderTicketIcon(row, section)}
                                    </td>
                                    {cells.map((cell, i) => {
                                        if(gateAt >= 0 && i > gateAt){
                                            return null;   // covered by the span
                                        }
                                        if(i === gateAt){
                                            return (
                                                <td key={`${row.slot}-gate`}
                                                    colSpan={cells.length - gateAt}
                                                    className="prepick-gatecell">
                                                    <div className="prepick-gate">
                                                        {this.visitMessage()}
                                                    </div>
                                                </td>
                                            );
                                        }
                                        return (
                                        <td key={`${row.slot}-${cell.period_id}`}>
                                            {this.renderCell(section, row, cell)}
                                        </td>
                                        );
                                    })}
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* SUR-450 — all five states, named. It carried three: the two
                    red ones were collapsed into a single "Out", and neither
                    action marker was explained at all, so the sun icon was the
                    one thing on the screen with no key to it. */}
                <div className="prepick-legend">
                    <span><i className="prepick-swatch" style={{background: "#1e7e34"}} /> Survived</span>
                    <span><i className="prepick-swatch" style={{background: "#a71d2a"}} /> Eliminated — team lost</span>
                    <span><i className="prepick-swatch" style={{background: "#a71d2a"}} /> NO PICK — no pick made</span>
                    <span><i className="prepick-swatch" style={{background: "#14161a"}} /> Picked, not yet settled</span>
                    <span><i className="prepick-swatch" style={{background: "#ffc107"}} /> PLAY — can play today</span>
                    <span><span className="prepick-legend-sun" role="img" aria-label="Sun">☀️</span> Play tomorrow</span>
                </div>
            </div>
        );
    }

    renderOther(section){
        return (
            <div className="prepick-section prepick-other" key={`o${section.promo_id}`}>
                {section.promo_logo_url
                    ? <img src={section.promo_logo_url} alt={section.promo_name} />
                    : null}
                <h2>{section.promo_name}</h2>
                <button
                    type="button"
                    className="btn btn-warning btn-lg"
                    onClick={() => this.onPlayOther(section)}
                >
                    Play Now
                </button>
            </div>
        );
    }

    render() {
        if(this.state.redirect){
            return <Navigate to={this.state.redirect.path} state={this.state.redirect.state} />;
        }

        return (
            <div className="prepick">
                {/* Unmounted while a modal is up: GameTimeout only resets on
                    touch, and a player reading the daily-limit message touches
                    nothing. Left mounted it bounces them to the lobby mid-read. */}
                {/* Kiosk only. A kiosk is a shared terminal and an idle session
                    holds it, so it is reclaimed. A web player is on their own
                    device, blocking nobody — and the exit path there is the
                    venue's return URL, which throws them out of the promotion
                    entirely for having read the screen too long. The pick screen
                    times out back to here; here there is nowhere gentler to go,
                    so on the web it does not time out at all.

                    Unmounted while a modal is up for the original reason:
                    GameTimeout only resets on touch, and a player reading the
                    daily-limit message touches nothing. */}
                {this.state.page_ready && !isWebPlay() && !this.state.info_modal.show ? (
                    <GameTimeout {...this} timeout={this.state.timeout}
                                 onTimeout={() => this.exitOrHome("cancelled")} />
                ) : null}

                {/* The QA clock lives here, because this is the screen whose
                    answer depends most on what day it is. Renders nothing
                    unless the override is enabled, which is nowhere but a
                    developer's machine. */}
                <SurvivorClockBar control />

                {this.state.page_ready ? (
                    <React.Fragment>
                        <p className="prepick-venue">
                            {this.state.venue ? this.state.venue.name : ""}
                        </p>

                        {this.state.promotions.length === 0 ? (
                            <div className="prepick-section">
                                <h2>Nothing running right now</h2>
                                <p>There are no promotions open at this venue today.</p>
                            </div>
                        ) : this.state.promotions.map(section => (
                            // game_type 4 is Survivor; everything else is a card.
                            section.game_type === 4
                                ? this.renderSurvivor(section)
                                : this.renderOther(section)
                        ))}

                        <div className="prepick-footer">
                            {/* SUR-460 — the same grid, pointed at the pool. */}
                            <button
                                type="button"
                                className="btn btn-outline-light"
                                onClick={() => this.setState({
                                    redirect: {
                                        path: "/survivor/leaderboard",
                                        state: {
                                            venue: this.state.venue,
                                            player: this.state.player
                                        }
                                    }
                                })}
                            >
                                Pool Standings
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-light"
                                onClick={() => this.exitOrHome("done")}
                            >
                                Done
                            </button>
                        </div>
                    </React.Fragment>
                ) : null}

                {/* SUR-434 — dismissible. Nothing has gone wrong; the player is
                    being told when they may come back. */}
                <Modal show={this.state.info_modal.show} centered
                       className="prepick-info"
                       onHide={() => this.closeInfo()}>
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center">
                            {this.state.info_modal.title}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {(this.state.info_modal.text || "").split(/\n/).map((line, i) => (
                            <p key={i} className="text-center mb-1">{line}</p>
                        ))}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => this.closeInfo()}>
                            OK
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Fatal only — its single action is Return to Lobby. */}
                {/* GEO-409 -- the ask. Without this nothing ever calls
                    navigator.geolocation, so a player standing in the building
                    is told to go and visit it and is never offered the chance
                    to prove otherwise. Centred over the window so it is
                    read before anything else; dismissible, because GEO-405
                    keeps the grid and standings reachable off site. */}
                <OnsiteGate
                    onsite={this.state.onsite}
                    promoId={this.gatePromoId()}
                    variant="overlay"
                    onVerified={(gate) => this.setState({onsite: gate}, () => this.load(null, true))} />
                <ErrorModal show={this.state.error_modal.show}
                            title={this.state.error_modal.title}
                            text={this.state.error_modal.text}
                            restart={this.state.error_modal.restart} />
            </div>
        );
    }
}

export default withRouter(PrePick);
